import React, { useState, useEffect, useRef } from 'react';
import { usePositionContext } from '../contexts/PositionContext';
import { useOrderContext } from '../contexts/OrderContext';
import { useAccountType } from '../contexts/AccountTypeContext';
import { useAccount } from '../contexts/AccountContext';
import { useTheme } from '../contexts/ThemeContext';

interface PositionPanelProps {
  isRightPanelVisible?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  isFullscreen?: boolean;
  onFullscreenToggle?: () => void;
  hasBeenManuallyResized?: boolean;
  positionPanelHeight?: number;
}

const PositionPanel: React.FC<PositionPanelProps> = ({
  isRightPanelVisible = false,
  onExpandedChange,
  isFullscreen = false,
  onFullscreenToggle,
  hasBeenManuallyResized = false,
  positionPanelHeight = 60,
}) => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [isPositionsExpanded, setIsPositionsExpanded] = useState(false);
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editSize, setEditSize] = useState('');
  
  // Get position data from context
  const { 
    openPositions, 
    pendingOrders, 
    closedPositions, 
    closePosition, 
    cancelOrder,
    editOrder
  } = usePositionContext();

  // Get order context for selected price
  const { selectedPrice, clearSelectedPrice } = useOrderContext();

  // Get account type and account data
  const { accountType } = useAccountType();
  const { accountBalance } = useAccount();

  // Track previous position count for auto-expand
  const prevPositionCountRef = useRef(openPositions.length);
  const autoExpandTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-fill price when selected from order book
  useEffect(() => {
    if (selectedPrice && editingOrderId) {
      setEditPrice(selectedPrice.toString());
      clearSelectedPrice(); // Clear the selected price after using it
    }
  }, [selectedPrice, editingOrderId, clearSelectedPrice]);

  // Auto-expand when new positions are added (disabled in fullscreen mode)
  useEffect(() => {
    // Don't auto-expand when in fullscreen mode
    if (isFullscreen) {
      return;
    }

    const currentPositionCount = openPositions.length;
    const prevPositionCount = prevPositionCountRef.current;

    // Check if a new position was added (count increased)
    if (currentPositionCount > prevPositionCount && currentPositionCount > 0) {
      
      // Clear any existing timeout
      if (autoExpandTimeoutRef.current) {
        clearTimeout(autoExpandTimeoutRef.current);
      }

      // Auto-expand the panel and set active tab to open-positions
      setActiveTab('open-positions');
      setIsPositionsExpanded(true);
      setIsManuallyExpanded(false); // Mark as automatic expansion
      onExpandedChange?.(true);

      // No auto-collapse - panel stays expanded permanently after trade execution
    }

    // Update the previous count
    prevPositionCountRef.current = currentPositionCount;

    // Cleanup timeout on unmount
    return () => {
      if (autoExpandTimeoutRef.current) {
        clearTimeout(autoExpandTimeoutRef.current);
      }
    };
  }, [openPositions.length, onExpandedChange, isFullscreen]);

  // Auto-collapse when there are no positions and panel is expanded (only if not manually expanded, disabled in fullscreen)
  useEffect(() => {
    // Don't auto-collapse when in fullscreen mode
    if (isFullscreen) {
      return;
    }

    if (openPositions.length === 0 && isPositionsExpanded && !isManuallyExpanded) {
      // Small delay to allow user to see the "No open positions" message
      const collapseTimeout = setTimeout(() => {
        setActiveTab(null);
        setIsPositionsExpanded(false);
        onExpandedChange?.(false);
      }, 2000); // Collapse after 2 seconds

      return () => clearTimeout(collapseTimeout);
    }
  }, [openPositions.length, isPositionsExpanded, isManuallyExpanded, onExpandedChange, isFullscreen]);



  const handleClosePosition = (positionId: string) => {
    // For demo purposes, use current price as close price
    // In real implementation, this would get the current market price
    const position = openPositions.find(p => p.id === positionId);
    if (position) {
      closePosition(positionId, position.current);
    }
  };

  const handleEditOrder = (orderId: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (order) {
      setEditingOrderId(orderId);
      setEditPrice(order.price);
      setEditSize(order.size);
    }
  };

  const handleSaveEdit = () => {
    if (editingOrderId && editPrice && editSize) {
      editOrder(editingOrderId, editPrice, editSize);
      setEditingOrderId(null);
      setEditPrice('');
      setEditSize('');
    }
  };

  const handleCancelEdit = () => {
    setEditingOrderId(null);
    setEditPrice('');
    setEditSize('');
  };

  const handleCancelOrder = (orderId: string) => {
    cancelOrder(orderId);
  };

  const tabs = [
    { 
      id: 'open-positions', 
      name: 'Open Positions', 
      icon: 'TrendingUp',
      count: openPositions.length
    },
    { 
      id: 'pending-orders', 
      name: 'Pending Orders', 
      icon: 'Clock',
      count: pendingOrders.length
    },
    { 
      id: 'finance', 
      name: 'Finance', 
      icon: 'Wallet',
      count: null // Finance tab doesn't need a count
    },
    { 
      id: 'close-position', 
      name: 'Close Position', 
      icon: 'X',
      count: closedPositions.length
    },
  ];

  // Get finance data based on current account type
  const getFinanceData = () => {
    const balance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
    const equity = accountBalance.equity;
    const margin = accountBalance.margin;
    const freeMargin = accountBalance.freeMargin;
    const marginLevel = accountBalance.marginLevel;
    const profit = accountBalance.profit;

    // Calculate different profit values for demo vs live accounts
    const baseProfit = accountType === 'demo' ? profit * 1.2 : profit; // Demo shows slightly higher profits
    const weeklyProfit = baseProfit * 0.3; // Weekly is 30% of base profit
    const monthlyProfit = baseProfit * 1.8; // Monthly is 180% of base profit

    return {
      balance: `$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      equity: `$${equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      margin: `$${margin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      freeMargin: `$${freeMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      marginLevel: `${marginLevel.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
      weeklyProfit: weeklyProfit >= 0 ? `+$${weeklyProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(weeklyProfit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      monthlyProfit: monthlyProfit >= 0 ? `+$${monthlyProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(monthlyProfit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    };
  };

  const financeData = getFinanceData();
  const { theme } = useTheme();

  // Calculate scrollable container height based on panel state
  const getScrollableHeight = () => {
    if (isFullscreen) {
      return { height: '555px' }; // Fullscreen height
    } else if (hasBeenManuallyResized && isPositionsExpanded) {
      // When manually resized, calculate height based on panel height
      // Subtract: tab header (~40px) + table header (~30px) + padding (~30px) = ~100px
      const headerAndPadding = 100;
      const availableHeight = Math.max(positionPanelHeight - headerAndPadding, 120); // Minimum 120px for scrollability
      
      // Ensure the height is reasonable and allows for scrolling
      // Allow up to 80% of viewport height for maximum usability
      const viewportHeight = window.innerHeight;
      const maxAllowedHeight = Math.floor(viewportHeight * 0.8); // 80% of screen height
      const maxHeight = Math.min(availableHeight, maxAllowedHeight);
      
      // Debug logging
      console.log('Position Panel Debug:', {
        positionPanelHeight,
        hasBeenManuallyResized,
        isPositionsExpanded,
        availableHeight,
        maxHeight,
        viewportHeight,
        maxAllowedHeight: maxAllowedHeight
      });
      
      return { height: `${maxHeight}px` };
    } else if (isPositionsExpanded) {
      return { height: '70px' }; // Default expanded height (2 rows)
    } else {
      return { maxHeight: '96px' }; // Collapsed height (24 * 4px = 96px)
    }
  };

  // Mock close positions data is now replaced by real data from PositionContext

  return (
    <div className="m-0 p-0">
    <div 
      className="theme-bg-secondary rounded-lg max-h-[40px]"
    >
      {/* Tab Navigation */}
      <div className="border-b theme-border">
        <div className="flex items-center justify-between px-2 mt-2">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  // Toggle behavior: if same tab is clicked, close it; otherwise open the new tab
                  if (activeTab === tab.id) {
                    setActiveTab(null);
                    setIsPositionsExpanded(false);
                    setIsManuallyExpanded(false);
                    onExpandedChange?.(false);
                  } else {
                    setActiveTab(tab.id);
                    setIsPositionsExpanded(true);
                    setIsManuallyExpanded(true);
                    onExpandedChange?.(true);
                  }
                }}
                className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-xs transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-[#2AB7A6] text-[#2AB7A6]'
                    : `border-transparent theme-text-quaternary ${
                        theme === 'light' 
                          ? 'hover:text-[#2AB7A6] hover:border-[#2AB7A6]' 
                          : 'hover:text-white hover:theme-border-secondary'
                      }`
                }`}
              >
                <span>{tab.name}</span>
                {tab.count !== null && tab.count > 0 && (
                  <span className="ml-1 text-xs opacity-75">({tab.count})</span>
                )}
              </button>
            ))}
          </nav>
          
          {/* Control Buttons */}
          <div className="flex items-center space-x-1">
            {/* Fullscreen Toggle Button */}
            <button
              onClick={() => {
                onFullscreenToggle?.();
                // Automatically activate first tab when entering fullscreen
                if (!isFullscreen) {
                  setActiveTab('open-positions');
                  setIsPositionsExpanded(true);
                  setIsManuallyExpanded(true);
                  onExpandedChange?.(true);
                }
              }}
              className="p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? (
                // Exit fullscreen icon
                <svg 
                  className="w-4 h-4 transition-colors duration-200" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9v4.5M15 9h4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15v-4.5M15 15h4.5M15 15l5.5 5.5" 
                    className="transition-colors duration-200 group-hover:stroke-[#2AB7A6]"
                  />
                </svg>
              ) : (
                // Enter fullscreen icon
                <svg 
                  className="w-4 h-4 transition-colors duration-200" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" 
                    className="transition-colors duration-200 group-hover:stroke-[#2AB7A6]"
                  />
                </svg>
              )}
            </button>
            
            {/* Collapse/Expand Toggle Button - Hidden in fullscreen mode */}
            {!isFullscreen && (
              <button
                onClick={() => {
                  // Clear auto-collapse timeout when manually toggling
                  if (autoExpandTimeoutRef.current) {
                    clearTimeout(autoExpandTimeoutRef.current);
                    autoExpandTimeoutRef.current = null;
                  }
                  
                  // Toggle expand/collapse state
                  const newExpanded = !isPositionsExpanded;
                  setIsPositionsExpanded(newExpanded);
                  setIsManuallyExpanded(newExpanded); // Mark as manual expansion/collapse
                  if (!newExpanded) {
                    setActiveTab(null); // Clear active tab when collapsing
                  } else {
                    // Automatically activate first tab when expanding
                    setActiveTab('open-positions');
                  }
                  onExpandedChange?.(newExpanded);
                }}
                className="p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
                title={isPositionsExpanded ? "Collapse panel" : "Expand panel"}
              >
              <svg 
                className={`w-4 h-4 transition-transform duration-200 ${isPositionsExpanded ? 'rotate-0' : 'rotate-180'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 9l-7 7-7-7" 
                />
              </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className={`p-3 pt-1 transition-all duration-300 ${
        activeTab && (isFullscreen || isPositionsExpanded)
          ? 'h-full overflow-y-auto' // Show content when tab is active and expanded
          : 'max-h-0 overflow-hidden' // Hide content when no tab is active
      }`}>
        {activeTab === 'open-positions' && (
          <div className={`${isFullscreen ? 'h-full flex flex-col' : 'space-y-2'}`}>
            {/* Table Header */}
            <div className="grid grid-cols-9 gap-2 text-xs font-medium theme-text-quaternary border-b theme-border-secondary pb-2 mt-3 flex-shrink-0">
              <div>Symbol</div>
              <div>Type</div>
              <div>Size</div>
              <div>Entry</div>
              <div>Current</div>
              <div>Margin</div>
              <div>Leverage</div>
              <div className="text-right">P&L</div>
              <div className="text-right">Action</div>
            </div>

            {/* Table Rows with Scrollable Container */}
            <div 
              className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
              style={{
                ...getScrollableHeight(),
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
              }}
            >
              {openPositions.length === 0 ? (
                <div className="text-center py-5 text-xs theme-text-quaternary ">
                  <span className="py-4">No open positions</span>
                </div>
              ) : (
                openPositions.map(position => {
                  // Calculate margin used for this position
                  const entryPrice = parseFloat(position.entry);
                  const size = parseFloat(position.size);
                  const leverage = position.leverage;
                  const marginUsed = (entryPrice * size) / leverage;
                  
                  return (
                    <div
                      key={position.id}
                      className="grid grid-cols-9 gap-2 text-xs py-2 px-2 border-b theme-border hover:theme-bg-quaternary/50 transition-colors duration-200 items-center"
                    >
                      <div className="font-medium theme-text-secondary flex items-center">
                        {position.symbol}
                        {position.isAI ? (
                          <span className="ml-1 px-1 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded" title="AI Trade">
                            AI
                          </span>
                        ) : (
                          <span className="ml-1 px-1 py-0.5 text-xs bg-green-500/20 text-green-400 rounded" title="Manual Trade">
                            MI
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        <div
                          className={`p-1 rounded mr-2 ${position.type === 'Buy' ? 'bg-[#2AB7A6]/20' : 'bg-red-500/20'}`}
                        >
                          <span className={`text-xs ${position.type === 'Buy' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                            {position.type === 'Buy' ? '↗' : '↘'}
                          </span>
                        </div>
                        <span
                          className={
                            position.type === 'Buy'
                              ? 'text-[#2AB7A6]'
                              : 'text-red-400'
                          }
                        >
                          {position.type}
                        </span>
                      </div>
                      <div className="theme-text-secondary">{position.size}</div>
                      <div className="theme-text-secondary">{position.entry}</div>
                      <div className="theme-text-secondary">{position.current}</div>
                      <div className="theme-text-secondary">${marginUsed.toFixed(2)}</div>
                      <div className="theme-text-secondary">
                        {position.leverage}x
                      </div>
                      <div className="text-right">
                        <div className={`font-medium ${position.pnl.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                          {position.pnl} ({position.pnlPercent})
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleClosePosition(position.id)}
                          className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300 rounded transition-colors duration-200"
                          title="Close Position"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'pending-orders' && (
          <div className={`${isFullscreen ? 'h-full flex flex-col' : 'space-y-2'}`}>
            {/* Table Header */}
            <div className="grid grid-cols-9 gap-2 text-xs font-medium theme-text-quaternary border-b theme-border-secondary pb-2 mt-3 flex-shrink-0">
              <div>Symbol</div>
              <div>Type</div>
              <div>Size</div>
              <div>Order Type</div>
              <div>Price</div>
              <div>Margin</div>
              <div>Leverage</div>
              <div>Status</div>
              <div className="text-right">Action</div>
            </div>

            {/* Table Rows with Scrollable Container */}
            <div 
              className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
              style={{
                ...getScrollableHeight(),
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
              }}
            >
              {pendingOrders.length === 0 ? (
                <div className="text-center py-4 text-xs theme-text-quaternary">
                  No pending orders
                </div>
              ) : (
                pendingOrders.map(order => {
                  // Calculate margin that would be used for this order
                  const orderPrice = parseFloat(order.orderType === 'stop' && order.stopPrice ? order.stopPrice : order.price);
                  const orderSize = parseFloat(order.size);
                  const leverage = order.leverage;
                  const marginUsed = (orderPrice * orderSize) / leverage;
                  
                  return (
                    <div
                      key={order.id}
                      className="grid grid-cols-9 gap-2 text-xs py-2 px-2 border-b theme-border hover:theme-bg-quaternary/50 transition-colors duration-200 items-center"
                    >
                      <div className="font-medium theme-text-secondary">
                        {order.symbol}
                      </div>
                      <div className="flex items-center">
                        <span
                          className={
                            order.type === 'Buy'
                              ? 'text-[#2AB7A6]'
                              : 'text-red-400'
                          }
                        >
                          {order.type}
                        </span>
                      </div>
                      <div className="theme-text-secondary">
                        {editingOrderId === order.id ? (
                          <input
                            type="number"
                            value={editSize}
                            onChange={(e) => setEditSize(e.target.value)}
                            className="w-full px-2 py-1 text-xs theme-bg-secondary theme-text-primary border theme-border-secondary rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Size"
                          />
                        ) : (
                          order.size
                        )}
                      </div>
                      <div className="theme-text-secondary">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          order.orderType === 'limit' 
                            ? 'bg-white/20 text-white' 
                            : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {order.orderType === 'limit' ? 'Limit' : 'Stop'}
                        </span>
                      </div>
                      <div className="theme-text-secondary">
                        {editingOrderId === order.id ? (
                          <input
                            type="number"
                            step="0.00001"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            className="w-full px-2 py-1 text-xs theme-bg-secondary theme-text-primary border theme-border-secondary rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Price"
                          />
                        ) : (
                          order.orderType === 'stop' && order.stopPrice ? order.stopPrice : order.price
                        )}
                      </div>
                      <div className="theme-text-secondary">${marginUsed.toFixed(2)}</div>
                      <div className="theme-text-secondary">
                        {leverage}x
                      </div>
                      <div className="flex items-center">
                        <span className="theme-text-secondary">{order.status}</span>
                      </div>
                      <div className="flex justify-end space-x-1">
                    {editingOrderId === order.id ? (
                      <>
                        <button
                          onClick={handleSaveEdit}
                          className="px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30 hover:text-green-300 rounded transition-colors duration-200"
                          title="Save Changes"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-2 py-1 text-xs font-medium bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 hover:theme-text-secondary rounded transition-colors duration-200"
                          title="Cancel Edit"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditOrder(order.id)}
                          className="px-2 py-1 text-xs font-medium bg-[#2AB7A6]/20 text-[#2AB7A6] hover:bg-[#2AB7A6]/30 hover:text-[#2AB7A6] rounded transition-colors duration-200"
                          title="Edit Order"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleCancelOrder(order.id)}
                          className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300 rounded transition-colors duration-200"
                          title="Cancel Order"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'finance' && (
          <div className="space-y-2">
            {/* Account Type Indicator */}
            <div className="flex items-center justify-center mb-2 mt-1.5 hidden">
              <div className="px-3 py-1 text-xs font-medium rounded-full bg-[#2AB7A6]/20 text-[#2AB7A6]">
                {accountType === 'demo' ? 'Demo Account' : 'Live Account'}
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-3 mt-5">
              <div className="p-4 py-2 theme-bg-quaternary rounded-lg">
                <p className="text-xs theme-text-quaternary">{accountType === 'demo' ? 'Demo Balance' : 'Account Balance'}</p>
                <p className="text-xs font-medium theme-text-primary">
                  {financeData.balance}
                </p>
              </div>
              <div className="p-4 py-2 theme-bg-quaternary rounded-lg">
                <p className="text-xs theme-text-quaternary">Equity</p>
                <p className="text-xs font-medium theme-text-primary">
                  {financeData.equity}
                </p>
              </div>
              <div className="p-4 py-2 theme-bg-quaternary rounded-lg">
                <p className="text-xs theme-text-quaternary">Margin</p>
                <p className="text-xs font-medium theme-text-primary">
                  {financeData.margin}
                </p>
              </div>
              <div className="p-4 py-2 theme-bg-quaternary rounded-lg">
                <p className="text-xs theme-text-quaternary">Free Margin</p>
                <p className="text-xs font-medium theme-text-primary">
                  {financeData.freeMargin}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="p-4 py-2 theme-bg-quaternary rounded-lg">
                <p className="text-xs theme-text-quaternary">Margin Level</p>
                <p className="text-xs font-medium theme-text-primary">
                  {financeData.marginLevel}
                </p>
              </div>
              <div className="p-4 py-2 theme-bg-quaternary rounded-lg">
                <p className="text-xs theme-text-quaternary">Current P&L</p>
                <p className={`text-xs font-medium ${accountBalance.profit >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                  {financeData.weeklyProfit}
                </p>
              </div>
              <div className="p-4 py-2 theme-bg-quaternary rounded-lg">
                <p className="text-xs theme-text-quaternary">Weekly Profit</p>
                <p className={`text-xs font-medium ${financeData.weeklyProfit.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                  {financeData.weeklyProfit}
                </p>
              </div>
              <div className="p-4 py-2 theme-bg-quaternary rounded-lg">
                <p className="text-xs theme-text-quaternary">Monthly Profit</p>
                <p className={`text-xs font-medium ${financeData.monthlyProfit.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                  {financeData.monthlyProfit}
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'close-position' && (
          <div className={`${isFullscreen ? 'h-full flex flex-col' : 'space-y-2'}`}>
            {/* Table Header */}
            <div className="grid gap-2 text-xs font-medium theme-text-quaternary border-b theme-border-secondary pb-2 mt-5 flex-shrink-0" 
                 style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr' }}>
              <div>Symbol</div>
              <div>Type</div>
              <div>Size</div>
              <div>Entry</div>
              <div>Close</div>
              <div>Close Time</div>
              <div className="text-center">Status</div>
              <div className="text-right">P&L</div>
            </div>

            {/* Table Rows with Scrollable Container */}
            <div 
              className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
              style={{
                ...getScrollableHeight(),
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
              }}
            >
              {closedPositions.length === 0 ? (
                <div className="text-center py-4 text-xs theme-text-quaternary">
                  No closed positions
                </div>
              ) : (
                closedPositions.map(position => (
                <div
                  key={position.id}
                  className="grid gap-2 text-xs py-2 px-2 border-b theme-border hover:theme-bg-quaternary/50 transition-colors duration-200 items-center"
                  style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr' }}
                >
                  <div className="font-medium theme-text-secondary flex items-center">
                    {position.symbol}
                    {position.isAI ? (
                      <span className="ml-1 px-1 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded" title="AI Trade">
                        AI
                      </span>
                    ) : (
                      <span className="ml-1 px-1 py-0.5 text-xs bg-green-500/20 text-green-400 rounded" title="Manual Trade">
                        MI
                      </span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <div
                      className={`p-1 rounded mr-2 ${position.type === 'Buy' ? 'bg-[#2AB7A6]/20' : 'bg-red-500/20'}`}
                    >
                      <span className={`text-xs ${position.type === 'Buy' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                        {position.type === 'Buy' ? '↗' : '↘'}
                      </span>
                    </div>
                    <span
                      className={
                        position.type === 'Buy'
                          ? 'text-[#2AB7A6]'
                          : 'text-red-400'
                      }
                    >
                      {position.type}
                    </span>
                  </div>
                  <div className="theme-text-secondary">{position.size}</div>
                  <div className="theme-text-secondary">{position.entry}</div>
                  <div className="theme-text-secondary">{position.close}</div>
                  <div className="theme-text-secondary text-xs">
                    {position.closeTime}
                  </div>
                  <div className="flex justify-center">
                    <div className={`px-2 py-1 text-xs font-medium rounded ${
                      position.status === 'Success' 
                        ? 'bg-[#2AB7A6]/20 text-[#2AB7A6]' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {position.status}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-medium ${position.pnl.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                      {position.pnl} ({position.pnlPercent})
                    </div>
                  </div>
                </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default PositionPanel;
